// 관리자 대시보드 데이터 — 전부 GET. 쓰기 함수는 두지 않는다.
// (관리자는 사용자의 대화를 볼 수만 있다. 서버에도 GET 외 엔드포인트가 없다.)
import { api } from "./api";

export interface Tokens {
  input: number;
  output: number;
  cache_read: number;
  cache_write: number;
}

export interface ToolStat {
  name: string;
  calls: number;
  fails: number;
  avg_ms: number | null;
}

export interface UserStat {
  user_uid: string;
  turns: number;
  conversations: number;
  errors: number;
  in_tok: number;
  out_tok: number;
  avg_ms: number;
  /** 최초 접속(가입) — 조회 기간과 무관한 전체 최솟값 */
  first_at?: string | null;
  /** 접속 기기 — "pc" · "mobile" · "tablet" 을 쉼표로 이은 값. 구글 베타
   *  로그인에서만 기록되므로 그 이전 사용자는 빈 문자열이다. */
  devices?: string | null;
  last_at: string | null;
}

export interface DailyStat {
  day: string;
  turns: number;
  users: number;
  conversations: number;
  errors: number;
  avg_ms: number;
  tokens: number;
}

export interface ModelStat {
  model: string;
  turns: number;
  in_tok: number;
  out_tok: number;
  cache_read: number;
  cache_write: number;
}

export interface Overview {
  days: number;
  totals: {
    turns: number;
    users: number;
    conversations: number;
    errors: number;
    avg_ms: number;
    tokens: Tokens;
    deleted_conversations: number;
  };
  daily: DailyStat[];
  tools: ToolStat[];
  labs?: { feature: string; uses: number; users: number; last_at: string | null }[];
  /** 기능별 개별 조회 내역 — 무엇을 조회했는지(회사명·사업내용·클릭한 이슈)까지 담긴다 */
  lab_recent?: { at: string | null; user_uid: string; feature: string; detail: string }[];
  models?: ModelStat[];
  users: UserStat[];
}

export interface LogItem {
  id: number;
  user_uid: string;
  conversation_id: string | null;
  turn: number | null;
  question: string;
  answer: string;
  model: string | null;
  tools: { name: string; ok: boolean; ms: number | null }[];
  files: { name?: string; url?: string }[];
  steps: number | null;
  duration_ms: number | null;
  error: string | null;
  tokens: Tokens;
  created_at: string | null;
}

interface Wrapped<T> {
  ok: boolean;
  data?: T;
  message?: string;
}

function qs(params: Record<string, string | number | boolean>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== "" && v !== undefined && v !== null) s.set(k, String(v));
  }
  const out = s.toString();
  return out ? "?" + out : "";
}

export async function fetchOverview(days: number): Promise<Overview> {
  const d = await api<Wrapped<Overview>>("/agent/admin/overview" + qs({ days }));
  if (!d.ok || !d.data) throw new Error(d.message || "조회에 실패했습니다.");
  return d.data;
}

export interface LogPage {
  total: number;
  items: LogItem[];
  limit: number;
  offset: number;
}

export type LogSort = "recent" | "tokens" | "duration";

export async function fetchLogs(p: {
  days: number;
  user?: string;
  q?: string;
  errors_only?: boolean;
  /** 사용 모델 필터 (정확 일치) */
  model?: string;
  /** 이 도구를 호출한 턴만 */
  tool?: string;
  /** 도구 결과: "1" 성공만 · "0" 실패만 · "" 전체 (tool 지정 시에만 의미) */
  tool_ok?: "" | "0" | "1";
  sort?: LogSort;
  limit?: number;
  offset?: number;
}): Promise<LogPage> {
  const d = await api<Wrapped<LogPage>>("/agent/admin/logs" + qs({
    days: p.days,
    user: p.user || "",
    q: p.q || "",
    errors_only: !!p.errors_only,
    model: p.model || "",
    tool: p.tool || "",
    tool_ok: p.tool_ok || "",
    sort: p.sort || "recent",
    limit: p.limit ?? 50,
    offset: p.offset ?? 0,
  }));
  if (!d.ok || !d.data) throw new Error(d.message || "조회에 실패했습니다.");
  return d.data;
}

export async function fetchConversation(id: string): Promise<LogItem[]> {
  const d = await api<Wrapped<LogItem[]>>(
    "/agent/admin/conversation/" + encodeURIComponent(id),
  );
  if (!d.ok || !d.data) throw new Error(d.message || "조회에 실패했습니다.");
  return d.data;
}

export interface RosterItem {
  email: string;
  added_by: string | null;
  created_at: string | null;
  /** 코드에 박힌 계정 — 대시보드에서 제거 불가 */
  builtin: boolean;
  /** 최고관리자(추가/제거 권한) */
  manager: boolean;
  /** 관리자 = 대시보드 열람 · 사용자 = 로그인만. 둘 다 Enterprise 구독 없이 로그인한다. 없으면 관리자. */
  role?: RosterRole;
}

export type RosterRole = "admin" | "user";

export async function fetchRoster(): Promise<RosterItem[]> {
  const d = await api<Wrapped<RosterItem[]>>("/agent/admin/roster");
  if (!d.ok || !d.data) throw new Error(d.message || "조회에 실패했습니다.");
  return d.data;
}

// 명단 변경은 접근 권한을 바꾸는 것이다 — 사용자의 대화에는 손대지 않는다.
// 서버(에이전트·valuation-2 양쪽)가 최고관리자인지 다시 판정한다.
export async function addAdmin(email: string, role: RosterRole = "admin"): Promise<void> {
  const d = await api<Wrapped<unknown>>("/agent/admin/roster", {
    method: "POST",
    body: JSON.stringify({ email, role }),
  });
  if (!d.ok) throw new Error(d.message || "추가에 실패했습니다.");
}

export async function removeAdmin(email: string, role: RosterRole = "admin"): Promise<void> {
  const d = await api<Wrapped<unknown>>(`/agent/admin/roster/${encodeURIComponent(email)}?role=${role}`, {
    method: "DELETE",
  });
  if (!d.ok) throw new Error(d.message || "제거에 실패했습니다.");
}

// 서비스 설정 — 접근 정책 토글 (첫 항목: 엔터프라이즈 전용 로그인).
// 이것도 대화가 아니라 접근 정책을 바꾼다. 쓰기는 서버가 최고관리자를 다시 판정한다.
export interface SettingItem {
  value: string;
  updated_by: string | null;
  updated_at: string | null;
  /** 비밀 키(gpt_api_key 등) — 값은 서버가 마스킹하고 설정 여부만 내려온다 */
  set?: boolean;
  last4?: string;
}

export async function fetchSettings(): Promise<Record<string, SettingItem>> {
  const d = await api<Wrapped<Record<string, SettingItem>>>("/agent/admin/settings");
  if (!d.ok || !d.data) throw new Error(d.message || "조회에 실패했습니다.");
  return d.data;
}

// ── 남용 대응 ────────────────────────────────────────────────────
// 스크래퍼 의심 계정. 자동 차단은 하지 않는다 — 오탐으로 거래처를 막는 게
// 수집당하는 것보다 큰 사고라, 판단은 관리자가 한다.
export interface AbuseRow {
  uid: string;
  requests: number;
  distinctKeywords: number;
  deepPages: number;
  signalLabels: string[];
  /** 신호 2개 이상 */
  suspect: boolean;
  /** epoch 초 */
  lastSeen: number | null;
  blocked: boolean;
}

export async function fetchAbuse(hours = 24): Promise<{ items: AbuseRow[]; blocked: string[] }> {
  const d = await api<Wrapped<{ items: AbuseRow[]; blocked: string[] }>>(
    "/agent/admin/abuse?hours=" + hours);
  if (!d.ok || !d.data) throw new Error(d.message || "조회에 실패했습니다.");
  return d.data;
}

/** 차단·해제 — 전 기능에서 403. 관리자가 풀기 전까지 유지된다. */
export async function setBlocked(uid: string, on: boolean): Promise<void> {
  const d = await api<Wrapped<unknown>>("/agent/admin/abuse/block", {
    method: "POST",
    body: JSON.stringify({ uid, on }),
  });
  if (!d.ok) throw new Error(d.message || "변경에 실패했습니다.");
}

/** 구글(베타) 계정 강제 로그아웃 — 다시 로그인하면 풀린다. */
export async function forceLogout(uid: string): Promise<void> {
  const d = await api<Wrapped<unknown>>("/agent/admin/abuse/logout", {
    method: "POST",
    body: JSON.stringify({ uid, on: true }),
  });
  if (!d.ok) throw new Error(d.message || "로그아웃에 실패했습니다.");
}

export interface UsageRow {
  user_uid: string;
  day: string; // YYYY-MM-DD
  feature: string; // chat | buffett | peer_ai | industry | precedent | dcf | report | re | re_report
  uses: number;
}

export async function fetchUsage(days: number): Promise<UsageRow[]> {
  const d = await api<Wrapped<UsageRow[]>>(`/agent/admin/usage?days=${days}`);
  if (!d.ok || !d.data) throw new Error(d.message || "조회에 실패했습니다.");
  return d.data;
}

export async function updateSetting(key: string, value: string): Promise<void> {
  const d = await api<Wrapped<unknown>>("/agent/admin/settings", {
    method: "PUT",
    body: JSON.stringify({ key, value }),
  });
  if (!d.ok) throw new Error(d.message || "저장에 실패했습니다.");
}

// MCP 재연결도 대화에는 손대지 않는다 — 외부 Graph DB 세션·도구 캐시만 새로 붙인다.
// graft 서버가 도구를 개편하면 실행 중인 백엔드는 재배포 전까지 옛 목록을 물고 있는데,
// 이 버튼이 재배포 없이 그 간극을 없앤다. 권한은 서버가 다시 본다(관리자 이상).
export interface McpReload {
  count: number;
  tools: string[];
  /** 서버 도구 개편 감지 — new: 미채택 신규, removed: 서버에서 사라진 도구 */
  sync?: { checked: boolean; new: string[]; removed: string[] };
}

export async function reloadMcp(): Promise<McpReload> {
  const d = await api<Wrapped<McpReload>>("/agent/admin/mcp/reload", { method: "POST" });
  if (!d.ok || !d.data) throw new Error(d.message || "재연결에 실패했습니다.");
  return d.data;
}

/** 큰 수를 읽기 쉽게 — 토큰은 자릿수가 커서 그대로 두면 비교가 안 된다 */
export function compact(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (Math.abs(n) >= 10_000) return Math.round(n / 1000) + "K";
  return n.toLocaleString("ko-KR");
}

export function ms(n: number | null): string {
  if (!n && n !== 0) return "–";
  return n >= 10_000 ? (n / 1000).toFixed(1) + "초" : Math.round(n) + "ms";
}

// 대시보드 시각은 항상 한국시간으로 보여준다 — 보는 사람이 어느 시간대에 있어도
// 같은 값이어야 운영 로그를 서로 대조할 수 있다.
const KST_FMT = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hour12: false,
});

export function when(iso: string | null): string {
  if (!iso) return "–";
  // 오프셋이 없는 문자열은 DB 의 naive UTC 다. 그대로 new Date() 에 넣으면
  // 브라우저가 로컬시간으로 읽어 9시간 어긋나므로 UTC 임을 명시해 준다.
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const d = new Date(hasZone ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return iso;
  // sv-SE 로케일은 "YYYY-MM-DD HH:mm" 형태를 준다
  return KST_FMT.format(d).replace(",", "");
}

// ── 모델 단가표 (USD / 1M tokens) — 추정 비용 계산용. 가격 개정 시 여기만 고친다.
// 등록되지 않은 모델은 토큰만 표시하고 비용은 "단가 미등록"으로 남긴다.
export const MODEL_PRICES: Record<string, { in: number; out: number; cacheRead: number; cacheWrite: number }> = {
  "claude-sonnet-5":  { in: 3.0, out: 15.0, cacheRead: 0.3, cacheWrite: 3.75 },
  "claude-haiku-5-5": { in: 0.1, out: 0.5,  cacheRead: 0.01, cacheWrite: 0.125 },
  // 과거 로그(모델명이 바뀌기 전)도 같은 단가로 계산되게 남긴다.
  "claude-haiku-4-5": { in: 1.0, out: 5.0,  cacheRead: 0.1, cacheWrite: 1.25 },
  // 게이트웨이(ds-modelhub) 단가 기준 — 2026-10-08 정정(예전 gpt-5.6-luna 값은 gpt-5 단가였다)
  "gpt-5.6-luna":     { in: 0.20, out: 1.20, cacheRead: 0.02, cacheWrite: 0.25 },
  "gpt-6-luna":       { in: 0.10, out: 0.50, cacheRead: 0.01, cacheWrite: 0.125 },
};

export function modelCostUsd(m: ModelStat): number | null {
  const p = MODEL_PRICES[m.model];
  if (!p) return null;
  return (
    (m.in_tok * p.in + m.out_tok * p.out + m.cache_read * p.cacheRead + m.cache_write * p.cacheWrite) /
    1_000_000
  );
}

export function usd(n: number): string {
  return "$" + (n >= 100 ? Math.round(n).toLocaleString("en-US") : n.toFixed(2));
}
